import { useUnsafeEffect } from "@wags/tanstack-start/react";
import { DogPhotoViewer, useDogPhotoViewer } from "@/features/dog-photo-viewer";
import { requestChatSuppression } from "@/features/kits-conversations";
import {
    DogFullBleedMediaPanel,
    DogListingPhotoWell,
    type DogPhotoWellState,
    type MediaPanelEndSlide,
} from "@/features/listings";
import {
    type ReactNode,
    type Ref,
    type RefObject,
    useSyncExternalStore,
} from "react";

export type DogGallery = {
    state: DogPhotoWellState;
    mediaItems: { url: string; mimeType?: string | null }[];
    alt: string;
    index: number;
    onIndexChange: (index: number) => void;
    active: boolean;
    mountGallery: boolean;
    endSlide: MediaPanelEndSlide | undefined;
};

export function DogMobileGallery({
    dogId,
    gallery: { state, ...gallery },
    priority,
    coveredBy,
}: {
    dogId: string;
    gallery: DogGallery;
    priority: boolean;
    coveredBy: readonly RefObject<Element | null>[];
}) {
    const viewer = useDogPhotoViewer();
    const viewerUp = useSyncExternalStore(
        viewer.subscribe,
        () => viewer.getSnapshot().mode !== "inline",
        () => false,
    );
    useUnsafeEffect(
        () => (viewerUp ? requestChatSuppression("photo-viewer") : undefined),
        [viewerUp],
    );
    return (
        <DogPhotoViewer
            key={dogId}
            viewer={viewer}
            coveredBy={coveredBy}
            media={gallery.mediaItems}
            mediaIndex={gallery.index}
            onMediaIndexChange={gallery.onIndexChange}
            title={gallery.alt}
        >
            <DogListingPhotoWell state={state}>
                <DogFullBleedMediaPanel
                    key={dogId}
                    scrollRestorationId={`listing-gallery-${dogId}-mobile`}
                    {...gallery}
                    onOpenFullscreen={viewer.openMedia}
                    priority={priority}
                />
            </DogListingPhotoWell>
        </DogPhotoViewer>
    );
}

export function DogMobileHero({
    children,
    sticker,
    aboveThumbnails,
    aboveThumbnailsRef,
}: {
    children: ReactNode;
    sticker?: ReactNode;
    aboveThumbnails?: ReactNode;
    aboveThumbnailsRef?: Ref<HTMLDivElement>;
}) {
    return (
        <div className="relative h-[68svh] w-full overflow-hidden bg-doberman-black md:h-[min(52svh,34rem)] md:[--listing-media-bottom-inset:var(--radius-surface)] dog-desktop:hidden">
            {children}
            {sticker && (
                <div className="pointer-events-none absolute top-[calc(env(safe-area-inset-top)+5rem)] left-5 z-10">
                    {sticker}
                </div>
            )}
            <div
                ref={aboveThumbnailsRef}
                className="pointer-events-none absolute inset-x-3.5 bottom-23 z-10"
            >
                {aboveThumbnails}
            </div>
        </div>
    );
}
